import React, { useState } from 'react';

/**
 * PersonalityType — MBTI personality type selector, display, and compatibility insights
 * 
 * Features:
 * - Select your 4-letter MBTI type from grouped dropdowns
 * - Link to 16personalities.com to take the test
 * - View your type's strengths, weaknesses, and romantic tendencies
 * - Compare with another person's type for compatibility insights
 */

// ─── Complete 16 Types Data ─────────────────────────────────────────
const MBTI_TYPES = {
  INTJ: {
    name: 'Architect',
    emoji: '🧠',
    group: 'Analysts',
    strengths: ['Strategic thinking', 'Independent', 'Determined', 'Innovative', 'Honest'],
    weaknesses: ['Overly critical', 'Dismissive of emotions', 'Can seem arrogant', 'Impatient'],
    romantic: 'Seeks intellectual connection above all. Loyal and committed once invested, but needs a partner who respects their independence and matches their depth.',
    loveLanguage: 'Quality time & acts of service',
  },
  INTP: {
    name: 'Logician',
    emoji: '🔬',
    group: 'Analysts',
    strengths: ['Analytical', 'Objective', 'Imaginative', 'Original', 'Open-minded'],
    weaknesses: ['Disconnected', 'Insensitive to feelings', 'Absent-minded', 'Overthinks'],
    romantic: 'Values intellectual stimulation and authenticity. May struggle expressing feelings but shows love through problem-solving and understanding their partner deeply.',
    loveLanguage: 'Quality time & words of affirmation',
  },
  ENTJ: {
    name: 'Commander',
    emoji: '👔',
    group: 'Analysts',
    strengths: ['Efficient', 'Energetic', 'Self-confident', 'Strong-willed', 'Strategic'],
    weaknesses: ['Stubborn', 'Intolerant', 'Impatient', 'Emotionally distant'],
    romantic: 'Approaches relationships with the same drive as everything else. Wants a capable partner they can respect and build an empire with.',
    loveLanguage: 'Words of affirmation & quality time',
  },
  ENTP: {
    name: 'Debater',
    emoji: '⚡',
    group: 'Analysts',
    strengths: ['Quick-witted', 'Charismatic', 'Energetic', 'Creative', 'Excellent brainstormer'],
    weaknesses: ['Argumentative', 'Insensitive', 'Intolerant of routine', 'Unfocused'],
    romantic: 'Keeps things exciting and never boring. Needs a partner who can match their mental energy and handle playful debates with good humour.',
    loveLanguage: 'Quality time & physical touch',
  },
  INFJ: {
    name: 'Advocate',
    emoji: '🌟',
    group: 'Diplomats',
    strengths: ['Insightful', 'Principled', 'Compassionate', 'Idealistic', 'Decisive'],
    weaknesses: ['Overly sensitive', 'Perfectionist', 'Burns out easily', 'Avoids confrontation'],
    romantic: 'The rarest type and one of the most devoted partners. Seeks deep soul-level connection and will invest enormously in the right relationship.',
    loveLanguage: 'Quality time & words of affirmation',
  },
  INFP: {
    name: 'Mediator',
    emoji: '🦋',
    group: 'Diplomats',
    strengths: ['Empathetic', 'Creative', 'Passionate', 'Open-minded', 'Loyal'],
    weaknesses: ['Overly idealistic', 'Self-isolating', 'Takes things personally', 'Impractical'],
    romantic: 'A true romantic at heart — seeks authentic, meaningful connection. Will love you in a way that feels like poetry, but needs emotional safety to open up fully.',
    loveLanguage: 'Words of affirmation & quality time',
  },
  ENFJ: {
    name: 'Protagonist',
    emoji: '🌈',
    group: 'Diplomats',
    strengths: ['Charismatic', 'Empathetic', 'Reliable', 'Natural leader', 'Altruistic'],
    weaknesses: ['Overly idealistic', 'Too selfless', 'Sensitive to criticism', 'Indecisive'],
    romantic: 'One of the most naturally romantic types. Warm, attentive, and devoted — they will make you feel like the most important person in the world.',
    loveLanguage: 'Words of affirmation & acts of service',
  },
  ENFP: {
    name: 'Campaigner',
    emoji: '🎪',
    group: 'Diplomats',
    strengths: ['Enthusiastic', 'Creative', 'Sociable', 'Optimistic', 'Free-spirited'],
    weaknesses: ['Disorganised', 'Overly accommodating', 'Easily stressed', 'Overthinks'],
    romantic: 'Brings infectious energy and spontaneity to relationships. Falls hard and fast, and loves with their whole being once they find their person.',
    loveLanguage: 'Physical touch & words of affirmation',
  },
  ISTJ: {
    name: 'Logistician',
    emoji: '📋',
    group: 'Sentinels',
    strengths: ['Responsible', 'Reliable', 'Patient', 'Honest', 'Methodical'],
    weaknesses: ['Stubborn', 'Judgemental', 'Rigid', 'Uncomfortable with change'],
    romantic: 'Shows love through actions, not words. The partner who remembers every anniversary, fixes things around the house, and will never let you down.',
    loveLanguage: 'Acts of service & quality time',
  },
  ISFJ: {
    name: 'Defender',
    emoji: '🛡️',
    group: 'Sentinels',
    strengths: ['Supportive', 'Reliable', 'Patient', 'Observant', 'Loyal'],
    weaknesses: ['Overly humble', 'Represses feelings', 'Overloads themselves', 'Resists change'],
    romantic: 'One of the most caring and nurturing types. Remembers every detail about you, anticipates your needs, and creates a warm, stable foundation.',
    loveLanguage: 'Acts of service & gifts',
  },
  ESTJ: {
    name: 'Executive',
    emoji: '🏛️',
    group: 'Sentinels',
    strengths: ['Organised', 'Dedicated', 'Strong-willed', 'Direct', 'Loyal'],
    weaknesses: ['Inflexible', 'Judgemental', 'Difficulty relaxing', 'Stubborn'],
    romantic: 'A dependable, no-nonsense partner. They show love by building structure and stability — the person who makes sure bills are paid and plans are made.',
    loveLanguage: 'Acts of service & quality time',
  },
  ESFJ: {
    name: 'Consul',
    emoji: '🤝',
    group: 'Sentinels',
    strengths: ['Caring', 'Sociable', 'Loyal', 'Sensitive to others', 'Warm'],
    weaknesses: ['Needy for approval', 'Inflexible', 'Vulnerable to criticism', 'Selfless to a fault'],
    romantic: "The ultimate partner for someone who wants to feel cared for. Generous, attentive, and deeply invested in their partner's happiness.",
    loveLanguage: 'Words of affirmation & acts of service',
  },
  ISTP: {
    name: 'Virtuoso',
    emoji: '🔧',
    group: 'Explorers',
    strengths: ['Resourceful', 'Calm in crisis', 'Practical', 'Spontaneous', 'Rational'],
    weaknesses: ['Stubborn', 'Insensitive', 'Private', 'Easily bored'],
    romantic: 'Quiet and grounded, but full of surprises. Shows affection through fixing your problems and doing things with you rather than talking about feelings.',
    loveLanguage: 'Acts of service & physical touch',
  },
  ISFP: {
    name: 'Adventurer',
    emoji: '🎨',
    group: 'Explorers',
    strengths: ['Charming', 'Sensitive', 'Imaginative', 'Passionate', 'Curious'],
    weaknesses: ['Fiercely private', 'Easily stressed', 'Unpredictable', 'Avoids conflict'],
    romantic: 'A gentle, artistic soul who loves through experiences. Will plan the most thoughtful, unexpected dates and make everyday moments feel special.',
    loveLanguage: 'Physical touch & quality time',
  },
  ESTP: {
    name: 'Entrepreneur',
    emoji: '🎯',
    group: 'Explorers',
    strengths: ['Bold', 'Direct', 'Sociable', 'Perceptive', 'Rational'],
    weaknesses: ['Impatient', 'Risk-prone', 'Unstructured', 'Defiant'],
    romantic: 'Exciting, spontaneous, and fully present. They live in the moment and will sweep you off your feet — but need a patient partner to go deeper.',
    loveLanguage: 'Physical touch & quality time',
  },
  ESFP: {
    name: 'Entertainer',
    emoji: '🎭',
    group: 'Explorers',
    strengths: ['Bold', 'Original', 'Aesthetic', 'Showmanship', 'Practical'],
    weaknesses: ['Sensitive', 'Conflict-averse', 'Easily bored', 'Unfocused'],
    romantic: 'The life of the party and the warmth in the room. Generous, fun-loving, and will make every date feel like an adventure.',
    loveLanguage: 'Physical touch & words of affirmation',
  },
};

const GROUPS = {
  Analysts: { emoji: '💜', types: ['INTJ', 'INTP', 'ENTJ', 'ENTP'] },
  Diplomats: { emoji: '💚', types: ['INFJ', 'INFP', 'ENFJ', 'ENFP'] },
  Sentinels: { emoji: '💙', types: ['ISTJ', 'ISFJ', 'ESTJ', 'ESFJ'] },
  Explorers: { emoji: '💛', types: ['ISTP', 'ISFP', 'ESTP', 'ESFP'] },
};

// ─── Compatibility Engine ───────────────────────────────────────────
const COMPATIBILITY_MAP = {
  // Golden pairs (natural partners)
  golden: [
    ['INFP', 'ENFJ'], ['ENFP', 'INFJ'], ['INTP', 'ENTJ'], ['ENTP', 'INTJ'],
    ['ISFP', 'ESFJ'], ['ESFP', 'ISFJ'], ['ISTP', 'ESTJ'], ['ESTP', 'ISTJ'],
  ],
  // Great matches
  great: [
    ['INFJ', 'ENFJ'], ['INFP', 'ENFP'], ['INTJ', 'ENTJ'], ['INTP', 'ENTP'],
    ['INFJ', 'INTJ'], ['INFP', 'INTP'], ['ENFJ', 'ENTJ'], ['ENFP', 'ENTP'],
    ['ISFJ', 'ESFJ'], ['ISFP', 'ESFP'], ['ISTJ', 'ESTJ'], ['ISTP', 'ESTP'],
    ['ENFJ', 'INFJ'], ['ENFP', 'INFJ'], ['INTJ', 'INFJ'],
  ],
  // Challenging (can work but requires effort)
  challenging: [
    ['ESTJ', 'INFP'], ['ISTJ', 'ENFP'], ['ESFJ', 'INTP'], ['ISFJ', 'ENTP'],
    ['ESTP', 'INFJ'], ['ISTP', 'ENFJ'], ['ESFP', 'INTJ'], ['ISFP', 'ENTJ'],
  ],
};

function getCompatibility(type1, type2) {
  if (!type1 || !type2) return null;
  if (type1 === type2) {
    return {
      level: 'mirror',
      score: 72,
      color: '#6366f1',
      label: 'Mirror Match',
      insight: `Two ${MBTI_TYPES[type1].name}s together! You'll understand each other intuitively but may amplify each other's blind spots. Great communication when you both share the same wavelength, but be mindful of reinforcing the same weaknesses.`,
    };
  }

  const pair = [type1, type2].sort();
  const isPairMatch = (arr) => arr.some(p => {
    const sorted = [...p].sort();
    return sorted[0] === pair[0] && sorted[1] === pair[1];
  });

  if (isPairMatch(COMPATIBILITY_MAP.golden)) {
    return {
      level: 'golden',
      score: 92,
      color: '#22c55e',
      label: 'Golden Pair 💛',
      insight: generateInsight(type1, type2, 'golden'),
    };
  }
  if (isPairMatch(COMPATIBILITY_MAP.great)) {
    return {
      level: 'great',
      score: 80,
      color: '#3b82f6',
      label: 'Great Match',
      insight: generateInsight(type1, type2, 'great'),
    };
  }
  if (isPairMatch(COMPATIBILITY_MAP.challenging)) {
    return {
      level: 'challenging',
      score: 55,
      color: '#f59e0b',
      label: 'Growth Match',
      insight: generateInsight(type1, type2, 'challenging'),
    };
  }

  // Default — moderate compatibility
  return {
    level: 'moderate',
    score: 68,
    color: '#8b5cf6',
    label: 'Interesting Dynamic',
    insight: generateInsight(type1, type2, 'moderate'),
  };
}

function generateInsight(type1, type2, level) {
  const t1 = MBTI_TYPES[type1];
  const t2 = MBTI_TYPES[type2];

  // Analyse cognitive differences
  const sharedLetters = [0,1,2,3].filter(i => type1[i] === type2[i]).length;
  const isEI = type1[0] !== type2[0]; // introvert-extrovert mix
  const isSN = type1[1] !== type2[1]; // sensing-intuition mix
  const isTF = type1[2] !== type2[2]; // thinking-feeling mix
  const isJP = type1[3] !== type2[3]; // judging-perceiving mix

  let parts = [];

  if (level === 'golden') {
    parts.push(`This is one of MBTI's most celebrated pairings — the ${t1.name} and ${t2.name} naturally complement each other's strengths and weaknesses.`);
  } else if (level === 'great') {
    parts.push(`The ${t1.name} and ${t2.name} share enough common ground to connect easily, while bringing different strengths to the table.`);
  } else if (level === 'challenging') {
    parts.push(`The ${t1.name} and ${t2.name} see the world quite differently — this can create real growth and spark, but takes patience from both sides.`);
  } else {
    parts.push(`The ${t1.name} and ${t2.name} bring an interesting mix of similarities and differences to the table.`);
  }

  if (isEI) {
    parts.push('The introvert-extrovert balance means one of you recharges through quiet time while the other thrives socially — learning to honour both needs is key.');
  }
  if (isTF) {
    parts.push('One of you leads with logic, the other with values and empathy — this can create a powerful balance where decisions consider both head and heart.');
  }
  if (isSN) {
    parts.push('You process information differently: one focuses on concrete details and present reality, the other on patterns and future possibilities. This can broaden both your perspectives.');
  }
  if (isJP) {
    parts.push('One prefers structure and plans, the other flexibility and spontaneity — finding middle ground here will be an ongoing but rewarding negotiation.');
  }
  if (sharedLetters >= 3) {
    parts.push('With so much in common, communication tends to feel effortless — just watch out for shared blind spots.');
  }

  return parts.join(' ');
}

// ─── Exported helper for discover cards ─────────────────────────────
export function getMbtiCompatibility(myType, theirType) {
  return getCompatibility(myType, theirType);
}

export { MBTI_TYPES };

// ─── Components ─────────────────────────────────────────────────────

/** Compact badge for discover cards and match lists */
export function MbtiBadge({ type, size = 'sm' }) {
  if (!type || !MBTI_TYPES[type]) return null;
  const t = MBTI_TYPES[type];
  const isSm = size === 'sm';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: isSm ? '2px 8px' : '4px 12px',
      borderRadius: 'var(--radius-full, 9999px)',
      background: 'rgba(99,102,241,0.1)',
      fontSize: isSm ? '11px' : '13px',
      fontWeight: 600,
      color: '#6366f1',
      whiteSpace: 'nowrap'
    }}>
      {t.emoji} {type}
    </span>
  );
}

/** Compatibility insight card for discover/match context */
export function MbtiCompatibilityCard({ myType, theirType, theirName }) {
  const [expanded, setExpanded] = useState(false);
  const compat = getCompatibility(myType, theirType);
  if (!compat || !myType || !theirType) return null;

  return (
    <div style={{
      background: 'var(--surface, #fff)',
      border: '1px solid var(--border, #e2e8f0)',
      borderRadius: 'var(--radius-md, 12px)',
      padding: '14px',
      marginTop: '10px'
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}
      >
        <div style={{
          width: '40px', height: '40px', borderRadius: '50%',
          background: compat.color, color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: 700, flexShrink: 0
        }}>
          {compat.score}%
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>
            {compat.label}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted, #94a3b8)' }}>
            {MBTI_TYPES[myType]?.name} + {MBTI_TYPES[theirType]?.name} • Tap for details
          </div>
        </div>
        <span style={{
          fontSize: '16px',
          transition: 'transform 0.2s',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>▾</span>
      </div>
      {expanded && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid var(--border-light, #f1f5f9)',
          fontSize: '13px',
          lineHeight: 1.7,
          color: 'var(--text-secondary, #64748b)'
        }}>
          {compat.insight}
        </div>
      )}
    </div>
  );
}

/** Full personality section for the profile editor */
export default function PersonalityType({ value, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  const currentType = value && MBTI_TYPES[value] ? value : null;
  const typeData = currentType ? MBTI_TYPES[currentType] : null;

  return (
    <div>
      {/* Current type display or prompt */}
      {currentType ? (
        <div>
          {/* Type header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '14px'
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--primary, #6366f1), #a855f7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '24px', flexShrink: 0
            }}>
              {typeData.emoji}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                {currentType}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted, #94a3b8)' }}>
                The {typeData.name} • {typeData.group}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPicker(!showPicker)}
              style={{
                background: 'var(--surface-alt, #f1f5f9)',
                border: '1px solid var(--border, #e2e8f0)',
                borderRadius: 'var(--radius-sm, 8px)',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-secondary, #64748b)',
                cursor: 'pointer'
              }}
            >
              Change
            </button>
          </div>

          {/* Love language */}
          <div style={{
            background: 'var(--surface-dim, #f8fafc)',
            borderRadius: 'var(--radius-sm, 8px)',
            padding: '10px 14px',
            marginBottom: '14px',
            fontSize: '13px'
          }}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>Love language: </span>
            <span style={{ color: 'var(--text-secondary, #64748b)' }}>{typeData.loveLanguage}</span>
          </div>

          {/* Romantic style */}
          <div style={{
            fontSize: '13px',
            lineHeight: 1.7,
            color: 'var(--text-secondary, #64748b)',
            marginBottom: '14px'
          }}>
            {typeData.romantic}
          </div>

          {/* Strengths & Weaknesses */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
            <div style={{
              background: '#f0fdf4',
              borderRadius: 'var(--radius-sm, 8px)',
              padding: '12px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#16a34a', marginBottom: '6px' }}>
                ✦ Strengths
              </div>
              {typeData.strengths.map((s, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#15803d', padding: '1px 0' }}>{s}</div>
              ))}
            </div>
            <div style={{
              background: '#fef2f2',
              borderRadius: 'var(--radius-sm, 8px)',
              padding: '12px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#dc2626', marginBottom: '6px' }}>
                ✦ Growth areas
              </div>
              {typeData.weaknesses.map((w, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#b91c1c', padding: '1px 0' }}>{w}</div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🧩</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary, #0f172a)', marginBottom: '4px' }}>
            What's your personality type?
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted, #94a3b8)', marginBottom: '14px' }}>
            Add your MBTI type to see compatibility insights with your matches
          </div>
        </div>
      )}

      {/* Test link */}
      <a
        href="https://www.16personalities.com/free-personality-test"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px',
          background: 'var(--surface-dim, #f8fafc)',
          border: '1px solid var(--border, #e2e8f0)',
          borderRadius: 'var(--radius-sm, 8px)',
          fontSize: '13px',
          fontWeight: 500,
          color: 'var(--primary, #6366f1)',
          textDecoration: 'none',
          marginBottom: '14px',
          cursor: 'pointer'
        }}
      >
        🔗 {currentType ? 'Retake' : 'Take'} the free 16Personalities test
      </a>

      {/* Type picker — show if no type set or user tapped "Change" */}
      {(showPicker || !currentType) && (
        <div>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--text-primary, #0f172a)',
            marginBottom: '10px'
          }}>
            {currentType ? 'Change your type:' : 'Select your type:'}
          </div>
          {Object.entries(GROUPS).map(([groupName, group]) => (
            <div key={groupName} style={{ marginBottom: '10px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'var(--text-muted, #94a3b8)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '6px'
              }}>
                {group.emoji} {groupName}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                {group.types.map((type) => {
                  const t = MBTI_TYPES[type];
                  const isSelected = type === currentType;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        onChange(type);
                        setShowPicker(false);
                      }}
                      style={{
                        padding: '8px 4px',
                        borderRadius: 'var(--radius-sm, 8px)',
                        border: isSelected
                          ? '2px solid var(--primary, #6366f1)'
                          : '1px solid var(--border, #e2e8f0)',
                        background: isSelected ? 'rgba(99,102,241,0.08)' : 'var(--surface, #fff)',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s'
                      }}
                    >
                      <div style={{ fontSize: '14px' }}>{t.emoji}</div>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? 'var(--primary, #6366f1)' : 'var(--text-primary, #0f172a)',
                        marginTop: '2px'
                      }}>{type}</div>
                      <div style={{
                        fontSize: '9px',
                        color: 'var(--text-muted, #94a3b8)',
                        marginTop: '1px'
                      }}>{t.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
